<template>
  <div id="app">
    <ChartLayout
      title="CO2 from energy combustion and industrial processes, Global Energy and Climate Model scenarios compared to IPCC scenarios with a temperature rise of 1.5 °C and 2.0 °C in 2100"
      units="Gt CO2"
    >
      <figure class="half-charts">
        <Chart
          :options="{
            ...options,
            subtitle: { text: 'IEA scenarios vs. IPCC 1.5°C scenarios' },
            series: series15,
          }"
        />
        <Chart
          :options="{
            ...options,
            subtitle: {
              text: 'IEA scenarios vs. IPCC 2.0°C scenarios',
            },
            yAxis: {
              title: { text: null },
              labels: { enabled: false },
              max: 50,
              min: -20,
              plotLines: [
                { value: 0, className: 'highcharts-zero-axis', zIndex: 99 },
              ],
            },
            series: series20,
          }"
        />
      </figure>
      <template #footer>
        <ChartLegend
          :legendItems="[
            { name: 'Net Zero Emissions by 2050 Scenario', colorIndex: 2 },
            { name: 'Announced Pledges Scenario', colorIndex: 5 },
          ]"
        />
      </template>
      <template #notes>
        <p>
          The 1.5°C scenarios are IPCC-vetted Category C1 scenarios (1.5°C with
          no or limited overshoot) and the 2°C IPCC Scenarios are IPCC-vetted
          Category C3 scenarios from the Sixth Assessment Report (AR6). Source:
          IIASA (International Institute for Applied Systems Analysis) (2022),
          AR6 Scenarios Database hosted by IIASA.
        </p>
      </template>
    </ChartLayout>
  </div>
</template>

<script>
import ChartOptions, {
  ChartLayout,
  ChartLegend,
} from "@international-energy-agency/vue-ui/highcharts";
import { Chart } from "highcharts-vue";
import weo from "./weo.json";
import ipcc from "./ipcc.json";
const series15 = ipcc[0].map((series, i) => ({
  name: `1.5 IPPC scenario ${i}`,
  colorIndex: 99,
  className: "highcharts-translucent",
  data: series.map((v) => (v === null ? null : v / 1000)),
}));
const series20 = ipcc[1].map((series, i) => ({
  name: `2.0 IPPC scenario ${i}`,
  colorIndex: 99,
  className: "highcharts-translucent",
  data: series.map((v) => (v === null ? null : v / 1000)),
}));

/**
 * Minimal component with Highcharts and IEA chart layout/options
 */
export default {
  name: "App",
  components: {
    ChartLayout,
    ChartLegend,
    Chart,
  },
  data() {
    // Any values that do NOT depend on other app values can be defined in "data",
    // otherwise they have to be defined as "computed" values
    return {
      series15: [
        ...series15,
        {
          name: "Net Zero Emissions by 2050 Scenario",
          colorIndex: 2,
          data: weo.NZE,
        },
        {
          name: "Announced Pledges Scenario",
          colorIndex: 5,
          data: weo.APS,
        },
      ],
      series20: [
        ...series20,
        {
          name: "Net Zero Emissions by 2050 Scenario",
          colorIndex: 2,
          data: weo.NZE,
        },
        {
          name: "Announced Pledges Scenario",
          colorIndex: 5,
          data: weo.APS,
        },
      ],
    };
  },
  computed: {
    options() {
      const { series15 } = this;
      /***
       * The ChartOptions factory will produce a basic Highcharts options object with sensible default values.
       * Any options you pass in the first parameter are merged onto the defaults, overwriting them if necessary.
       * */

      return ChartOptions.buildOptions(
        {
          tooltip: { enabled: false },
          plotOptions: {
            line: {
              connectNulls: false,
              pointStart: 2010,
              showInLegend: false,
            },
          },
          yAxis: {
            max: 50,
            min: -20,
          },
          xAxis: {
            plotLines: [
              {
                value: 2022,
                label: { text: "2022" },
                className: "highcharts-dotted",
              },
            ],
          },
        },
        "line"
      );
    },
  },
};
</script>

<style scoped>
.half-charts {
  display: flex;
  flex-wrap: wrap;
}
.half-charts > div {
  flex: 1 0 50%;
  min-width: 400px;
}
</style>